import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  Paragraph,
} from '@app/shared/ui/atoms/Common';
import { CarouselApi } from '@app/shared/ui/atoms/Common/Carousel';
import { FrontendEnergyStone } from '@app/shared/types/pages/stones';
import { Stone } from '@app/shared/ui/molecules/Common';
import { cognitionModalImage } from '@app/assets';

interface StoneCarouselProps {
  stones: FrontendEnergyStone[];
  onStoneChange: (stoneId: number) => void;
  onBuyBtnClick: () => void;
  selectedIndex: number;
  isBuyDisabled: boolean;
  onGiftClick: (nodeLevel: number) => void;
}

export const StoneCarousel = ({
  stones,
  onStoneChange,
  selectedIndex,
  onBuyBtnClick,
  isBuyDisabled,
  onGiftClick,
}: StoneCarouselProps) => {
  const { t } = useTranslation();
  const [api, setApi] = useState<CarouselApi | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (api) {
      api.scrollTo(selectedIndex);
    }
  }, [selectedIndex, api]);

  useEffect(() => {
    if (!api) return;
    const handleSelect = () => {
      onStoneChange(api.selectedScrollSnap());
    };
    api.on('select', handleSelect);
    return () => {
      api.off('select', handleSelect);
    };
  }, [api, onStoneChange]);

  const handleNavigate = (stoneId: number, matrixIndex: number) => {
    void navigate(`/matrix/channels/${String(stoneId)}/${String(matrixIndex)}`);
  };

  return (
    <Carousel className="overflow-hidden" opts={{ loop: true }} setApi={setApi}>
      <CarouselContent>
        <CarouselItem
          key={stones.length}
          className="flex flex-col items-center gap-4 whitespace-pre-line text-center"
        >
          <img src={cognitionModalImage} className="size-50" alt="stone diamond icon" />
          <Paragraph variant="white">{t('cognition.modal_description')}</Paragraph>
        </CarouselItem>
        {stones.map((stone) => (
          <CarouselItem key={stone.id} className="grid grid-rows-[1fr_auto] gap-4 text-center">
            <Stone
              nodeLevel={stone.nodeLevel}
              image={stone.imageUrl}
              imageClassName="size-40"
              title={stone.name}
              isPurchased={stone.isPurchased}
              titleClassName="text-xl text-foreground my-0"
              description={stone.description}
              onGiftClick={onGiftClick}
            />

            {!stone.isPurchased && (
              <Button disabled={isBuyDisabled} onClick={onBuyBtnClick}>
                {isBuyDisabled ? t('common.wait') : `${t('common.buy')} ${String(stone.price)} 🍪`}
              </Button>
            )}

            {stone.isPurchased && (
              <Button
                disabled={isBuyDisabled}
                onClick={() => {
                  handleNavigate(stone.id, stone.abilities[0].matrixIndex);
                }}
              >
                {t('common.read')}
              </Button>
            )}
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
};
